import axios from 'axios';

import authEndpoints from 'config/api/auth';
import logEndpoints from 'config/api/log';
import { setAuthorizationHeader } from 'services/authHeaders/authHeaders';
import { initializeAuthRefreshSynchronization, refreshAuthTokens } from 'services/authRefreshCoordinator/browserAuthRefreshCoordinator';
import accessTokenStorage from 'storages/accessTokenStorage';

const urlsWithoutRefresh = [authEndpoints.login().url, authEndpoints.refresh().url, logEndpoints.logEndpoint];

export const refreshToken = () => refreshAuthTokens();

/**
 * DRF authenticates before invoking the endpoint handler, so replay after an
 * authentication 401 cannot duplicate a handler side effect. The marker is
 * still mandatory: every original request, including POST/PATCH/DELETE, may be
 * replayed at most once and a second 401 is returned to the caller unchanged.
 */
export const createAuthResponseErrorHandler =
  ({ httpClient, refresh }) =>
  async error => {
    const status = error?.response?.status;
    const originalRequest = error?.config || error?.response?.config;
    const url = originalRequest?.url || '';

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._authRetry ||
      urlsWithoutRefresh.some(disabledUrl => url.includes(disabledUrl))
    ) {
      return Promise.reject(error);
    }

    originalRequest._authRetry = true;
    try {
      const authorization = await refresh();
      originalRequest.headers = { ...originalRequest.headers, Authorization: authorization };
      return httpClient(originalRequest);
    } catch {
      // The coordinator owns terminal logout and preserves auth on transient
      // failures. API callers keep receiving their original rejected request.
      return Promise.reject(error);
    }
  };

const axiosSetup = () => {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  axios.defaults.headers.common.Timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  setAuthorizationHeader(accessTokenStorage.get());
  initializeAuthRefreshSynchronization();

  const handleAuthResponseError = createAuthResponseErrorHandler({ httpClient: axios, refresh: refreshToken });
  axios.interceptors.response.use(response => response, handleAuthResponseError);
};

export default axiosSetup;
